import { IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import EditIcon from "src/assets/svg-icons/aggregators/edit.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useEffect, useState } from "react";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { useDeleteOCTransactionMutation } from "src/api/operation-cost/transactions";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import AddEditTransaction from "../../add-edit-transaction-popup";
import AttachmentsPopup from "../../attachments-popup";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import AddPaymentPopup from "../../add-payment-popup";
import { useTranslation } from "react-i18next";
import { specificBranchHasPermission } from "src/utils";
import { OCPermissions } from "src/constants/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

interface IAction {
  row: any;
  refetch: Function;
  isDeprecationInvoice: boolean;
  isExpensesOnly?: boolean;
}

const TableActions = (props: IAction) => {
  const navigate = useNavigate();
  const { row, refetch, isDeprecationInvoice, isExpensesOnly } = props;
  const { id, reference, can_delete, delete_message, type, is_received } = row;
  const { t } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [open, setOpen] = useState(false);
  const [openPay, setOpenPay] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [receivedBill, setReceivedBill] = useState(false);

  const isExpensesAndVariable = isExpensesOnly && type === "variable";

  //permissions
  const editPermission = specificBranchHasPermission(
    OCPermissions.editOC,
    row.branch_id,
  );
  const deletePermission = specificBranchHasPermission(
    OCPermissions.deleteOC,
    row.branch_id,
  );
  const payPermission = specificBranchHasPermission(
    OCPermissions.payOC,
    row.branch_id,
  );

  // APIS
  const { mutate, isPending, status, error } = useDeleteOCTransactionMutation();

  const handleOpenDelete = () => {
    if (isExpensesAndVariable) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Can't be deleted. Please delete it from the main bill",
        },
      ]);
      return;
    }
    if (!can_delete) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: delete_message ?? "Can not be deleted",
        },
      ]);
      return;
    }
    setOpen(true);
  };
  const handleCloseDelete = () => setOpen(false);

  const handleOpenPay = () => setOpenPay(true);
  const handleClosePay = () => setOpenPay(false);

  const handleOpenEdit = (type: "edit" | "receivedBill") => {
    if (type === "receivedBill") {
      setReceivedBill(true);
    } else {
      setReceivedBill(false);
    }
    setOpenEdit(true);
  };
  const handleCloseEdit = () => setOpenEdit(false);

  const handleOpenAttachmentsModal = () => setOpenAttachmentsModal(true);

  const handleCloseAttachmentsModal = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setOpenAttachmentsModal(false);
  };

  const handleDelete = () => {
    mutate(id);
  };

  const navigateToTransDetails = () => {
    navigate(`/operation/transaction/${id}`);
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleCloseDelete();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("deletedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Stack
      direction={"row"}
      spacing={"12px"}
      width={"100%"}
      justifyContent={"center"}
    >
      <IconButton size="small" onClick={(e) => handleClick(e)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {!isExpensesOnly && (
          <MenuItem onClick={handleOpenAttachmentsModal} sx={{ gap: 1 }}>
            <img src={DownloadIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("attachments")}
            </Typography>
          </MenuItem>
        )}
        {editPermission && !isExpensesOnly && (
          <MenuItem onClick={() => handleOpenEdit("edit")} sx={{ gap: 1 }}>
            <img src={EditIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("edit")}
            </Typography>
          </MenuItem>
        )}
        {deletePermission && (
          <MenuItem onClick={handleOpenDelete} sx={{ gap: 1 }}>
            <img src={DeleteIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {generalT("delete")}
            </Typography>
          </MenuItem>
        )}
        <MenuItem onClick={navigateToTransDetails} sx={{ gap: 1 }}>
          <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {generalT("details")}
          </Typography>
        </MenuItem>

        {payPermission &&
          row?.status !== "Paid" &&
          row?.status !== "Reminder" &&
          !isExpensesOnly &&
          !isDeprecationInvoice && (
            <MenuItem onClick={handleOpenPay} sx={{ gap: 1 }}>
              <PaidOutlinedIcon sx={{ fontSize: "16px" }} />
              <Typography
                color={"#1D2939"}
                fontSize={"16px"}
                textTransform={"capitalize"}
              >
                {`${generalT("pay")}!`}
              </Typography>
            </MenuItem>
          )}

        {(type === "variable" || type === "fixed") && !is_received && (
          <MenuItem
            onClick={() => handleOpenEdit("receivedBill")}
            sx={{ gap: 1 }}
          >
            <ReceiptLongOutlinedIcon sx={{ fontSize: "16px" }} />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("receivedABill")}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* payment popup */}
      {openPay && (
        <AddPaymentPopup
          open={openPay}
          handleClose={handleClosePay}
          refetch={refetch}
          id={id}
          row={row}
        />
      )}
      {/* confirm delete popup */}
      <ConfirmPopup
        open={open}
        item={reference}
        handleClose={handleCloseDelete}
        handleConfirm={handleDelete}
        isPending={isPending}
      />
      {/* edit popup */}
      {openEdit && (
        <AddEditTransaction
          open={openEdit}
          handleClose={handleCloseEdit}
          row={row}
          refetch={refetch}
          receivedBill={receivedBill}
        />
      )}
      {/* attachments popup */}
      {openAttachmentsModal && (
        <AttachmentsPopup
          open={openAttachmentsModal}
          handleClose={handleCloseAttachmentsModal}
          rowId={row.id}
        />
      )}
    </Stack>
  );
};
export default TableActions;
